/**
 * SleepOverlayScene — transient overlay launched by the dedicated Sleep key.
 *
 * SMA-52 — full bedtime ritual (extends B-016):
 *
 *   1. **Settling-in beat** (1.2 s) — soft fade-in of the title text "Rest?"
 *      with a quiet sub-line. No buttons yet. The character has *just* lain
 *      down; the player is not yet asked to commit.
 *
 *   2. **Dream-intent prompt** — a single-line text field appears with the
 *      label *"What shall the dream be about?"* (Dream-Weaving §1.1). The
 *      player can type a thought and Enter to set it, or skip with the
 *      sub-line *"Set nothing down."*. Submit fires `MSG_DREAM_WEAVE`; the
 *      next Dreaming-state Whisper accrual reads the intent and biases
 *      the picker (server seam B-038). Empty input is silently accepted
 *      as a "private" dream per `dream-weaving.md` §3.3.
 *
 *   3. **Action menu** — the original two paths plus *Back*:
 *        - *Stay until dawn*   — emits `MSG_SLEEP_COMPRESS_TO_DAWN`. The
 *                                server-side time-compression handler is
 *                                still a follow-up; the message id is
 *                                stable so a future handler attaches
 *                                without a client change.
 *        - *Sleep until you return* — closes the Colyseus session.
 *                                `OverworldRoom.onLeave` flips the player
 *                                to Dreaming; the next login granted-
 *                                whisper broadcast triggers the wake
 *                                animation (see OverworldScene).
 *        - *Back*              — dismisses without action.
 *
 * Esc / Back dismisses without action and resumes the underlying scene
 * from any phase.
 *
 * Lullaby SFX is intentionally NOT wired here — the audio key is a
 * Wave-N blocker. When the audio asset lands, fold it into create()
 * behind a `this.sound` guard.
 *
 * Pure helpers (`buildSleepActions`, `MSG_SLEEP_COMPRESS_TO_DAWN`) are
 * exported separately so the action wiring is unit-testable without a
 * Phaser harness — see `SleepOverlayScene.test.ts`.
 */

import * as Phaser from 'phaser';
import { PAINTED_WORLD_COORDINATOR_SCENE_KEY, SLEEP_OVERLAY_SCENE_KEY } from './sceneKeys';
import { buildSleepActions } from './sleepOverlayActions';
import { emitDayChanged } from '../game/HudState';
import { advanceDay } from '../ui/c5/dayCount';
import type { SleepOverlayDeps, SleepActions } from './sleepOverlayActions';
import { EVT_SHARED_DREAM } from '../systems/MilestoneEventSystem';
import { DREAM_INTENT_MAX_LENGTH, type MilestoneSharedDreamPayload } from '@game/shared';

// Re-export pure helpers so existing imports from this module keep working.
export { MSG_SLEEP_COMPRESS_TO_DAWN, buildSleepActions } from './sleepOverlayActions';
export type { SleepOverlayDeps, SleepActions } from './sleepOverlayActions';

const TITLE_FONT = "'Almendra', serif";
const COLOR_INK = '#eae0c8';
const COLOR_INK_DIM = '#9d927c';
const COLOR_INK_HOVER = '#ffffff';
const BUTTON_W = 280;
const BUTTON_H = 36;

/** Settling-in beat duration before the intent prompt fades in. */
const PRELUDE_FADE_MS = 1200;
/** Cross-fade duration between phases (intent → actions). */
const PHASE_FADE_MS = 600;

export class SleepOverlayScene extends Phaser.Scene {
  public static readonly KEY = SLEEP_OVERLAY_SCENE_KEY;

  /** Lazily-built actions; re-used by all phases of the overlay. */
  private actions: SleepActions | null = null;
  /** Container holding the intent-prompt UI; cleaned up on phase advance. */
  private intentLayer: Phaser.GameObjects.Container | null = null;
  /** Container holding the action-menu UI; cleaned up on shutdown. */
  private actionsLayer: Phaser.GameObjects.Container | null = null;
  /** Hidden DOM input that backs the intent prompt. */
  private intentInput: HTMLInputElement | null = null;
  /** True once the intent has been committed (or skipped) so we never
   *  double-fire MSG_DREAM_WEAVE on re-press. */
  private intentSubmitted = false;

  constructor() {
    super({ key: SLEEP_OVERLAY_SCENE_KEY });
  }

  create(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    // Semi-transparent backdrop — blocks input to the paused world scene.
    const backdrop = this.add
      .rectangle(cx, cy, this.scale.width, this.scale.height, 0x000000, 0.7)
      .setInteractive();
    backdrop.setScrollFactor(0);

    const title = this.add
      .text(cx, cy - 110, 'Rest?', {
        fontFamily: TITLE_FONT,
        fontSize: '48px',
        color: COLOR_INK,
      })
      .setOrigin(0.5, 0.5)
      .setAlpha(0);

    const settling = this.add
      .text(cx, cy - 60, 'You settle in.', {
        fontFamily: TITLE_FONT,
        fontSize: '18px',
        color: COLOR_INK_DIM,
        fontStyle: 'italic',
      })
      .setOrigin(0.5, 0.5)
      .setAlpha(0);

    this.actions = buildSleepActions(this.collectDeps());

    // Esc / B also dismiss — keep the verb cheap to back out of from
    // any phase.
    const esc = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    esc?.on('down', () => this.actions?.dismiss());

    // Phase 1 — settling-in fade-in.
    this.tweens.add({
      targets: [title, settling],
      alpha: 1,
      duration: PRELUDE_FADE_MS,
      ease: 'Sine.easeOut',
    });

    // Phase 2 — after the prelude, fade in the intent prompt.
    this.time.delayedCall(PRELUDE_FADE_MS, () => {
      this.tweens.add({
        targets: settling,
        alpha: 0,
        duration: PHASE_FADE_MS,
        ease: 'Sine.easeIn',
        onComplete: () => settling.destroy(),
      });
      this.showIntentPrompt(cx, cy);
    });

    // Phase OO — Shared Dream: listen for the milestone event on the
    // global game bus and render dream fragments additively if it fires
    // while this overlay is open.
    this.game.events.on(EVT_SHARED_DREAM, this.onSharedDream, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.off(EVT_SHARED_DREAM, this.onSharedDream, this);
      this.disposeIntentInput();
    });
  }

  /** Build the intent-prompt UI — label, hidden DOM input, *Set down* /
   *  *Skip* buttons, and a hint about what the prompt does. */
  private showIntentPrompt(cx: number, cy: number): void {
    if (!this.actions) return;

    const layer = this.add.container(0, 0);
    layer.setAlpha(0);
    this.intentLayer = layer;

    const label = this.add
      .text(cx, cy - 60, 'What shall the dream be about?', {
        fontFamily: TITLE_FONT,
        fontSize: '20px',
        color: COLOR_INK,
        fontStyle: 'italic',
      })
      .setOrigin(0.5, 0.5);
    layer.add(label);

    // The text field itself: a tinted rect with a real DOM <input> on top
    // so we get IME / paste / cursor for free, without standing up a
    // Phaser DOMElement plugin (which the project doesn't use elsewhere).
    const fieldBg = this.add
      .rectangle(cx, cy - 12, 360, 32, 0x0a0a0a, 0.85)
      .setStrokeStyle(1, 0x6c624a, 1);
    layer.add(fieldBg);

    this.intentInput = this.makeIntentInput(cx, cy - 12);

    const hint = this.add
      .text(cx, cy + 20, 'Whispers may listen.', {
        fontFamily: TITLE_FONT,
        fontSize: '13px',
        color: COLOR_INK_DIM,
        fontStyle: 'italic',
      })
      .setOrigin(0.5, 0.5);
    layer.add(hint);

    const setBtn = this.makeButton(cx - 80, cy + 64, 'Set it down', () =>
      this.commitIntentAndAdvance(),
    );
    const skipBtn = this.makeButton(cx + 80, cy + 64, 'Skip', () =>
      this.skipIntentAndAdvance(),
    );
    layer.add([setBtn.hit, setBtn.label, skipBtn.hit, skipBtn.label]);

    this.tweens.add({
      targets: layer,
      alpha: 1,
      duration: PHASE_FADE_MS,
      ease: 'Sine.easeOut',
    });

    // Enter on the text field counts as "Set it down". The DOM listener
    // is removed when the input is disposed in disposeIntentInput().
    this.intentInput?.addEventListener('keydown', this.handleIntentKeyDown);
  }

  /** Bound DOM listener so it can be removed on dispose. */
  private readonly handleIntentKeyDown = (ev: KeyboardEvent): void => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.commitIntentAndAdvance();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      this.actions?.dismiss();
    }
  };

  /** Submit the intent if non-empty, then transition to the action menu. */
  private commitIntentAndAdvance(): void {
    if (this.intentSubmitted) return;
    this.intentSubmitted = true;
    const text = this.intentInput?.value ?? '';
    this.actions?.submitIntent(text);
    this.advanceToActions();
  }

  /** Skip the intent (silently treated as "private" per spec §3.3). */
  private skipIntentAndAdvance(): void {
    if (this.intentSubmitted) return;
    this.intentSubmitted = true;
    this.advanceToActions();
  }

  /** Cross-fade the intent prompt out and the action menu in. */
  private advanceToActions(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    if (this.intentLayer) {
      const layer = this.intentLayer;
      this.tweens.add({
        targets: layer,
        alpha: 0,
        duration: PHASE_FADE_MS,
        ease: 'Sine.easeIn',
        onComplete: () => {
          layer.destroy(true);
        },
      });
    }
    this.disposeIntentInput();

    this.time.delayedCall(PHASE_FADE_MS, () => {
      this.showActionMenu(cx, cy);
    });
  }

  /** Build the original three-button action menu. Cross-faded in after
   *  the intent prompt resolves. */
  private showActionMenu(cx: number, cy: number): void {
    if (!this.actions) return;
    const layer = this.add.container(0, 0);
    layer.setAlpha(0);
    this.actionsLayer = layer;

    const stay = this.makeButton(cx, cy - 8, 'Stay until dawn', this.actions.stayUntilDawn);
    const sleep = this.makeButton(
      cx,
      cy + 36,
      'Sleep until you return',
      this.actions.sleepUntilReturn,
    );
    const back = this.makeButton(cx, cy + 96, 'Back', this.actions.dismiss);
    layer.add([stay.hit, stay.label, sleep.hit, sleep.label, back.hit, back.label]);

    this.tweens.add({
      targets: layer,
      alpha: 1,
      duration: PHASE_FADE_MS,
      ease: 'Sine.easeOut',
    });
  }

  /** Bridge the overlay to the OverworldScene's room without coupling. */
  private collectDeps(): SleepOverlayDeps {
    const overworld = this.scene.get(PAINTED_WORLD_COORDINATOR_SCENE_KEY) as
      | (Phaser.Scene & {
          sendRoomMessage?: (type: string, payload: Record<string, unknown>) => void;
          leaveRoom?: () => void;
        })
      | null;

    return {
      sendMessage: (type, payload) => overworld?.sendRoomMessage?.(type, payload),
      leaveRoom: () => {
        overworld?.leaveRoom?.();
        this.scene.stop(SLEEP_OVERLAY_SCENE_KEY);
      },
      dismiss: () => {
        this.scene.resume(PAINTED_WORLD_COORDINATOR_SCENE_KEY);
        this.scene.stop(SLEEP_OVERLAY_SCENE_KEY);
      },
      onNightPassed: () => {
        emitDayChanged(this.game, advanceDay());
      },
    };
  }

  /**
   * Render Shared Dream fragments sequentially with pauses between each.
   * Each fragment fades in, holds for 4 s, then fades out before the next.
   */
  private onSharedDream(payload: MilestoneSharedDreamPayload): void {
    const fragments = payload.fragments;
    if (!fragments || fragments.length === 0) return;

    const cx = this.scale.width / 2;
    const baseY = this.scale.height * 0.85;
    const HOLD_MS = 4_000;
    const FADE_MS = 1_500;
    const STEP_MS = HOLD_MS + FADE_MS * 2;

    fragments.forEach((fragment, i) => {
      this.time.delayedCall(i * STEP_MS, () => {
        const text = this.add.text(cx, baseY, fragment, {
          fontFamily: TITLE_FONT,
          fontSize: '22px',
          color: COLOR_INK,
          align: 'center',
          fontStyle: 'italic',
          wordWrap: { width: Math.min(520, this.scale.width - 80) },
        });
        text.setOrigin(0.5, 0.5);
        text.setAlpha(0);
        text.setDepth(10);

        this.tweens.add({
          targets: text,
          alpha: 1,
          duration: FADE_MS,
          ease: 'Sine.easeOut',
        });
        this.tweens.add({
          targets: text,
          alpha: 0,
          delay: FADE_MS + HOLD_MS,
          duration: FADE_MS,
          ease: 'Sine.easeIn',
          onComplete: () => text.destroy(),
        });
      });
    });
  }

  /** Build the DOM-backed text input and overlay it on top of the canvas
   *  at the canvas-coordinates that match the placeholder rect. The DOM
   *  input is positioned with CSS so it tracks the canvas exactly. */
  private makeIntentInput(canvasX: number, canvasY: number): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = DREAM_INTENT_MAX_LENGTH;
    input.placeholder = 'wind, bread, a friend… or nothing';
    input.autocomplete = 'off';
    input.spellcheck = false;
    Object.assign(input.style, {
      position: 'absolute',
      width: '340px',
      height: '24px',
      padding: '2px 8px',
      background: 'transparent',
      color: '#eae0c8',
      border: 'none',
      outline: 'none',
      font: `16px ${TITLE_FONT}`,
      fontStyle: 'italic',
      textAlign: 'center',
      pointerEvents: 'auto',
      zIndex: '1000',
    } satisfies Partial<CSSStyleDeclaration>);

    const canvas = this.game.canvas;
    const parent = canvas.parentElement ?? document.body;
    parent.appendChild(input);

    const positionInput = (): void => {
      const rect = canvas.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();
      const scaleX = rect.width / this.scale.width;
      const scaleY = rect.height / this.scale.height;
      const left = rect.left - parentRect.left + canvasX * scaleX - (340 * scaleX) / 2;
      const top = rect.top - parentRect.top + canvasY * scaleY - (24 * scaleY) / 2;
      input.style.left = `${left}px`;
      input.style.top = `${top}px`;
      input.style.width = `${340 * scaleX}px`;
      input.style.height = `${24 * scaleY}px`;
      input.style.fontSize = `${16 * scaleY}px`;
    };
    positionInput();
    window.addEventListener('resize', positionInput);
    // Stash the listener on the element so dispose can remove it.
    (input as unknown as { __positionInput?: () => void }).__positionInput = positionInput;

    // Auto-focus so the player can just start typing after the prelude.
    setTimeout(() => input.focus(), 50);
    return input;
  }

  /** Tear down the DOM input and any associated listeners. Idempotent. */
  private disposeIntentInput(): void {
    if (!this.intentInput) return;
    const input = this.intentInput;
    const positionInput = (input as unknown as { __positionInput?: () => void }).__positionInput;
    if (positionInput) window.removeEventListener('resize', positionInput);
    input.removeEventListener('keydown', this.handleIntentKeyDown);
    input.remove();
    this.intentInput = null;
  }

  private makeButton(
    x: number,
    y: number,
    text: string,
    onClick: () => void,
  ): { hit: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text } {
    const hit = this.add
      .rectangle(x, y, BUTTON_W, BUTTON_H, 0x000000, 0)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    const label = this.add.text(x, y, text, {
      fontFamily: TITLE_FONT,
      fontSize: '20px',
      color: COLOR_INK,
    });
    label.setOrigin(0.5, 0.5);

    hit.on('pointerover', () => label.setColor(COLOR_INK_HOVER));
    hit.on('pointerout', () => label.setColor(COLOR_INK));
    hit.on('pointerdown', onClick);

    return { hit, label };
  }
}
